import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, CircularProgress, MenuItem, TextField } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import DevopsRecordShell from "../components/devops/DevopsRecordShell";
import { devopsService } from "../api/services/devopsService";
import { useAuth } from "../contexts/AuthContext";
import { useDevopsWorkspace } from "../contexts/DevopsWorkspaceContext";
import { EntityStatus } from "../types";
import type {
  DevopsApplication,
  DevopsConfiguration,
  DevopsEnvironment,
  DevopsRelease,
  ReleaseStatus,
} from "../types/devops";
import { canMaintainRelease } from "../utils/devopsAccess";
import { devopsRoutes, type DevopsRecordMode } from "../utils/devopsRoutes";
import { getTenantId } from "../utils/jwtUtils";

const RELEASE_STATUSES: ReleaseStatus[] = [
  "PENDING",
  "IN_PROGRESS",
  "SUCCEEDED",
  "FAILED",
  "ROLLED_BACK",
];

interface ReleaseRecordPageProps {
  mode: DevopsRecordMode;
}

const ReleaseRecordPage: React.FC<ReleaseRecordPageProps> = ({ mode }) => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { bindEntity } = useDevopsWorkspace();
  const canWrite = canMaintainRelease(user?.roles ?? []);
  const readOnly = mode === "view";

  const [item, setItem] = useState<DevopsRelease | null>(null);
  const [environments, setEnvironments] = useState<DevopsEnvironment[]>([]);
  const [applications, setApplications] = useState<DevopsApplication[]>([]);
  const [configurations, setConfigurations] = useState<DevopsConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [version, setVersion] = useState("");
  const [environmentUuid, setEnvironmentUuid] = useState("");
  const [releaseStatus, setReleaseStatus] = useState<ReleaseStatus>("PENDING");
  const [changelog, setChangelog] = useState("");
  const [owner, setOwner] = useState(user?.email ?? "");
  const [status, setStatus] = useState<EntityStatus>(EntityStatus.ACTIVE);
  const [applicationUuids, setApplicationUuids] = useState<string[]>([]);
  const [configurationUuids, setConfigurationUuids] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode === "edit" && !canWrite && uuid) {
      navigate(devopsRoutes.releaseView(uuid), { replace: true });
    }
  }, [mode, canWrite, uuid, navigate]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [envs, apps, configs, release] = await Promise.all([
          devopsService.listEnvironments(),
          devopsService.listApplications(),
          devopsService.listConfigurations(),
          mode === "create" || !uuid ? Promise.resolve(null) : devopsService.getRelease(uuid),
        ]);
        if (cancelled) return;
        setEnvironments(envs);
        setApplications(apps);
        setConfigurations(configs);
        if (mode !== "create") {
          if (!release) {
            setError("Release not found.");
            return;
          }
          setItem(release);
          setName(release.name);
          setVersion(release.version);
          setEnvironmentUuid(release.environment.uuid);
          setReleaseStatus(release.releaseStatus);
          setChangelog(release.changelog ?? "");
          setOwner(release.owner);
          setStatus(release.status);
          setApplicationUuids((release.applications ?? []).map((app) => app.uuid));
          setConfigurationUuids((release.configurations ?? []).map((cfg) => cfg.uuid));
          bindEntity({ type: "RELEASE", uuid: release.uuid, name: release.name });
        } else {
          setEnvironmentUuid(envs[0]?.uuid ?? "");
        }
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load release");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [mode, uuid, bindEntity]);

  const envApps = useMemo(() => {
    const env = environments.find((e) => e.uuid === environmentUuid);
    const mapped = new Set((env?.applications ?? []).map((app) => app.uuid));
    return applications.filter((app) => mapped.has(app.uuid));
  }, [applications, environments, environmentUuid]);

  const envConfigs = useMemo(
    () => configurations.filter((cfg) => cfg.environment.uuid === environmentUuid),
    [configurations, environmentUuid],
  );

  const handleSave = async () => {
    const tenantId = getTenantId();
    if (!tenantId) {
      setError("No tenant on the current session.");
      return;
    }
    if (!name.trim() || !version.trim() || !owner.trim() || !environmentUuid) {
      setError("Name, version, owner, and environment are required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: name.trim(),
        version: version.trim(),
        releaseStatus,
        changelog,
        owner: owner.trim(),
        status,
        applicationUuids,
        configurationUuids,
      };
      const saved = item
        ? await devopsService.updateRelease(item.uuid, payload)
        : await devopsService.createRelease({
            tenantId,
            environmentUuid,
            ...payload,
          });
      navigate(devopsRoutes.releaseView(saved.uuid));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to save release");
    } finally {
      setSaving(false);
    }
  };

  const title =
    mode === "create"
      ? "Add release"
      : mode === "edit"
        ? `Edit ${item?.name ?? "release"}`
        : item ? `${item.name} ${item.version}` : "Release";

  if (loading) return <CircularProgress size={24} />;

  return (
    <DevopsRecordShell
      title={title}
      backPath={mode === "edit" && uuid ? devopsRoutes.releaseView(uuid) : devopsRoutes.releases}
      backLabel={mode === "edit" ? "Back to view" : "Back to list"}
      actions={
        mode === "view" && canWrite && uuid ? (
          <Button variant="contained" onClick={() => navigate(devopsRoutes.releaseEdit(uuid))}>
            Edit
          </Button>
        ) : undefined
      }
    >
      {error && <Alert severity="error">{error}</Alert>}
      <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required disabled={readOnly} />
      <TextField label="Version" value={version} onChange={(e) => setVersion(e.target.value)} required disabled={readOnly} />
      <TextField
        select
        label="Environment"
        value={environmentUuid}
        onChange={(e) => setEnvironmentUuid(e.target.value)}
        disabled={readOnly || Boolean(item)}
      >
        {environments.map((env) => (
          <MenuItem key={env.uuid} value={env.uuid}>
            {env.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label="Release status"
        value={releaseStatus}
        onChange={(e) => setReleaseStatus(e.target.value as ReleaseStatus)}
        disabled={readOnly}
      >
        {RELEASE_STATUSES.map((value) => (
          <MenuItem key={value} value={value}>
            {value}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        SelectProps={{ multiple: true }}
        label="Applications"
        value={applicationUuids}
        onChange={(e) =>
          setApplicationUuids(
            typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value,
          )
        }
        helperText="Only applications already mapped into this environment."
        disabled={readOnly}
      >
        {envApps.map((app) => (
          <MenuItem key={app.uuid} value={app.uuid}>
            {app.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        SelectProps={{ multiple: true }}
        label="Configurations"
        value={configurationUuids}
        onChange={(e) =>
          setConfigurationUuids(
            typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value,
          )
        }
        disabled={readOnly}
      >
        {envConfigs.map((cfg) => (
          <MenuItem key={cfg.uuid} value={cfg.uuid}>
            {cfg.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField label="Owner" value={owner} onChange={(e) => setOwner(e.target.value)} required disabled={readOnly} />
      <TextField
        select
        label="Status"
        value={status}
        onChange={(e) => setStatus(e.target.value as EntityStatus)}
        disabled={readOnly}
      >
        {Object.values(EntityStatus).map((value) => (
          <MenuItem key={value} value={value}>
            {value}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Changelog"
        value={changelog}
        onChange={(e) => setChangelog(e.target.value)}
        multiline
        minRows={4}
        disabled={readOnly}
      />
      {!readOnly && (
        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ alignSelf: "flex-start" }}>
          Save
        </Button>
      )}
    </DevopsRecordShell>
  );
};

export default ReleaseRecordPage;
